import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const LAST_UPDATED = "February 2026";
const ENTITY = "PowerSaver TX, operated by Jeffrey Nwankwo, Independent ACN Representative";
const CONTACT_EMAIL = "jeffreynwankwo8@gmail.com";
const CONTACT_PHONE = "(713) 205-2971";

function LegalLayout({ title, children, testid }) {
    return (
        <div className="min-h-screen" data-testid={testid}>
            <Navbar />
            <section className="max-w-3xl mx-auto px-4 sm:px-6 py-14">
                <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white">{title}</h1>
                <p className="mt-2 text-xs uppercase tracking-wider text-slate-500">Last updated: {LAST_UPDATED}</p>
                <div className="mt-6 space-y-5 text-slate-300 leading-relaxed text-sm">
                    {children}
                </div>
            </section>
            <Footer />
        </div>
    );
}

function H({ children }) {
    return <h3 className="text-white font-semibold mt-8">{children}</h3>;
}

export function PrivacyPage() {
    return (
        <LegalLayout title="Privacy Policy" testid="privacy-page">
            <p>
                {ENTITY} ("PowerSaver TX", "we", "us", "our") respects your privacy. This
                Privacy Policy explains what information we collect, how we use it, who we
                share it with, and the choices and rights you have. By using this site or
                submitting a form, you agree to this policy.
            </p>

            <H>Who we are</H>
            <p>
                PowerSaver TX is an independent consumer-education and lead-matching service
                based in Texas, operated by Jeffrey Nwankwo as an Independent Representative
                of ACN Opportunity, LLC. We are not a utility, a retail electric provider
                (REP), or a PUCT-regulated broker. We help households and small businesses
                compare the options available in their area and connect them with licensed
                providers.
            </p>

            <H>Information we collect</H>
            <p>
                We only collect the information you choose to share with us, including:
            </p>
            <ul className="list-disc pl-6 space-y-1">
                <li>Name, email address, phone number, ZIP code</li>
                <li>Customer type (homeowner, renter, moving, business) and service interests</li>
                <li>Preferred contact method, appointment time, and language preference</li>
                <li>Referral codes and the referring person's code, if used</li>
                <li>Basic technical data: IP address, device/browser type, pages visited, and ad-click identifiers from platforms like Meta, TikTok, and Google (used for ad measurement only)</li>
            </ul>

            <H>How we use your information</H>
            <ul className="list-disc pl-6 space-y-1">
                <li>Contact you about electricity plan options and the service(s) you asked about</li>
                <li>Schedule and confirm callbacks, appointments, and reminders</li>
                <li>Deliver welcome messages, follow-ups, and testimonial requests you opted into</li>
                <li>Match you with a licensed retail electric provider or ACN-affiliated service</li>
                <li>Operate the site, measure ad performance, and prevent abuse or fraud</li>
                <li>Comply with legal obligations</li>
            </ul>

            <H>SMS & phone communications (TCPA)</H>
            <p>
                By providing your phone number and selecting "text" or "phone" as your
                preferred contact method, you expressly consent to receive calls and text
                messages (including via automated technology) from PowerSaver TX at the
                number you provided, for purposes including appointment scheduling,
                reminders, service follow-up, and testimonial requests. Consent is not a
                condition of purchase. Message frequency varies. <strong>Msg &amp; data
                rates may apply.</strong> Reply <strong>STOP</strong> at any time to
                unsubscribe. Reply <strong>HELP</strong> for help. Carriers are not liable
                for delayed or undelivered messages.
            </p>

            <H>How we share information</H>
            <p>
                <strong>We do not sell your personal information.</strong> We share your
                information only with:
            </p>
            <ul className="list-disc pl-6 space-y-1">
                <li>Licensed retail electric providers (REPs) and ACN-affiliated service partners you asked to be matched with</li>
                <li>Service providers that help us operate the site (hosting, email delivery, SMS delivery via Twilio, analytics)</li>
                <li>Law enforcement or regulators when required by law or valid legal process</li>
            </ul>
            <p>
                We require every partner that handles your data to use it only for the
                purpose we share it for.
            </p>

            <H>Cookies & advertising</H>
            <p>
                We use cookies and pixels from Meta (Facebook), TikTok, and Google to
                measure ad performance and show relevant ads. You can disable third-party
                cookies in your browser settings or opt out of personalized advertising at
                each platform's preferences page. We do not use cookies to collect sensitive
                information.
            </p>

            <H>Your California privacy rights (CCPA/CPRA)</H>
            <p>
                If you are a California resident, you have the right to: (1) know what
                personal information we collect and how we use it; (2) request a copy of
                your personal information; (3) request deletion of your personal
                information; (4) correct inaccurate information; and (5) opt out of the
                "sale" or "sharing" of your personal information. <strong>We do not sell
                personal information and we do not share it for cross-context behavioral
                advertising outside of standard ad-measurement cookies.</strong> To exercise
                any right, email <a className="text-emerald-400 underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> or
                call {CONTACT_PHONE}. We will verify your identity before fulfilling the
                request. We will not discriminate against you for exercising a privacy right.
            </p>

            <H>Do Not Sell or Share My Personal Information</H>
            <p>
                To opt out, email <a className="text-emerald-400 underline" href={`mailto:${CONTACT_EMAIL}?subject=Do%20Not%20Sell%20or%20Share`}>{CONTACT_EMAIL}</a> with
                the subject line "Do Not Sell or Share" and include the name, email, and
                phone number you used on our forms. We will process the request within 15
                business days.
            </p>

            <H>Data retention</H>
            <p>
                We keep lead information for up to 24 months after your last interaction so
                we can serve follow-up requests and comply with record-keeping rules. We
                keep call/SMS consent records for as long as required by law. You can
                request deletion at any time (see above).
            </p>

            <H>Security</H>
            <p>
                We take reasonable administrative and technical steps to protect the
                information you share, including TLS encryption in transit, hashed admin
                credentials, and access controls on our lead database. No internet-based
                service can be 100% secure; we encourage you not to send sensitive
                information (SSN, bank/card numbers) through our forms.
            </p>

            <H>Children's privacy</H>
            <p>
                Our service is intended for adults 18+. We do not knowingly collect
                information from children under 13.
            </p>

            <H>Changes to this policy</H>
            <p>
                We may update this policy from time to time. The "Last updated" date at the
                top will reflect the latest version. Material changes will be noted on this
                page.
            </p>

            <H>Contact us</H>
            <p>
                Questions, requests, or complaints: email <a className="text-emerald-400 underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> or
                call/text {CONTACT_PHONE}.
            </p>
        </LegalLayout>
    );
}

export function TermsPage() {
    return (
        <LegalLayout title="Terms of Use" testid="terms-page">
            <p>
                These Terms of Use ("Terms") are a binding agreement between you and {ENTITY}
                ("PowerSaver TX", "we", "us"). By accessing this site or submitting any form,
                you agree to these Terms. If you don't agree, please don't use the site.
            </p>

            <H>Who we are</H>
            <p>
                PowerSaver TX is an independent consumer-education and lead-matching service.
                We are <strong>not</strong> a utility, a retail electric provider, a PUCT-licensed
                broker, or an aggregator. We help you compare the options available in your
                area and connect you with licensed providers who serve you directly.
            </p>

            <H>ACN affiliate disclosure</H>
            <p>
                Jeffrey Nwankwo is an Independent Representative of ACN Opportunity, LLC.
                When a visitor enrolls in a qualifying service through an ACN-affiliated
                partner, Jeffrey may earn commissions or referral compensation from ACN or
                the partner provider. This does <strong>not</strong> change the price you
                pay — rates, terms, and promotions are set by the provider. You are free to
                shop and enroll with any provider, through or outside of PowerSaver TX.
            </p>

            <H>No guarantees — informational only</H>
            <p>
                Information on this site is provided for comparison and education. We make
                no guarantee of specific savings, pricing, plan availability, eligibility,
                or outcomes. Rates, fees, contract terms, early-termination fees, and
                availability are set solely by the retail electric provider and vary by
                address, usage, and credit. You are responsible for reviewing the provider's
                Electricity Facts Label (EFL), Terms of Service (TOS), and Your Rights as a
                Customer (YRAC) before enrolling.
            </p>

            <H>Communications consent</H>
            <p>
                By submitting a form, you consent to be contacted by the method you selected
                (phone, text, or email) about electricity plan options and related services.
                See our Privacy Policy for full SMS/TCPA disclosure and opt-out instructions.
            </p>

            <H>Eligibility</H>
            <p>
                You must be at least 18 years old and legally able to enter a service
                contract in Texas to use this site. By submitting a form, you confirm that
                the contact information you provide belongs to you or that you have
                authorization to use it.
            </p>

            <H>Acceptable use</H>
            <p>
                You agree not to: (a) submit false or someone else's contact information;
                (b) use the site to send spam, scrape data, or interfere with normal
                operation; (c) attempt to access non-public areas of the site; (d) use the
                site in violation of any law.
            </p>

            <H>Third-party sites & providers</H>
            <p>
                The site may link to third-party sites or match you with third-party
                providers. We are not responsible for their content, pricing, policies, or
                conduct. Your relationship with any provider you enroll with is strictly
                between you and that provider.
            </p>

            <H>Intellectual property</H>
            <p>
                All PowerSaver TX branding, copy, and page design are owned by us or our
                licensors. You may not copy, resell, or republish site content without our
                written permission.
            </p>

            <H>Disclaimer of warranties</H>
            <p>
                The site is provided "as is" and "as available", without warranties of any
                kind, express or implied, including merchantability, fitness for a
                particular purpose, and non-infringement. We don't warrant that the site
                will be uninterrupted, error-free, or free of viruses.
            </p>

            <H>Limitation of liability</H>
            <p>
                To the fullest extent allowed by law, PowerSaver TX and Jeffrey Nwankwo will
                not be liable for any indirect, incidental, special, consequential, or
                punitive damages, or for lost profits or savings, arising out of or related
                to your use of the site or any provider you were matched with. Our total
                aggregate liability for any claim will not exceed one hundred US dollars
                ($100).
            </p>

            <H>Indemnification</H>
            <p>
                You agree to defend and indemnify PowerSaver TX and Jeffrey Nwankwo against
                any claim arising from your misuse of the site, your breach of these Terms,
                or your violation of any law or third-party right.
            </p>

            <H>Governing law & disputes</H>
            <p>
                These Terms are governed by the laws of the State of Texas, without regard
                to conflict-of-laws principles. Any dispute will be brought in the state or
                federal courts located in Harris County, Texas, and you consent to the
                personal jurisdiction of those courts.
            </p>

            <H>Changes</H>
            <p>
                We may update these Terms. Material changes will be reflected by the "Last
                updated" date at the top. Continued use after a change means you accept the
                revised Terms.
            </p>

            <H>Contact</H>
            <p>
                Questions about these Terms: email <a className="text-emerald-400 underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> or
                call/text {CONTACT_PHONE}.
            </p>
        </LegalLayout>
    );
}
